// =============================================
// File: src/components/charts/MetricTrendChart.jsx
// Purpose: Interactive sparkline-style trend chart for derived metrics
//
// Key features:
// - Optional time-proportional X spacing (linear scale) via xValues (ms timestamps)
// - Optional daily min/max bars (MinMaxBarsPlugin)
// - Hover tooltip shows the point value, and (optionally) min/max for that day
// - Click a point to toggle-remove/restore it from the series
// - Shift/Cmd/Ctrl-click supports multi-select (visual only; removal still toggles clicked point)
// - Removed points are rendered as "missing" so they are excluded from line + y-range
// - Optional highlight points rendered as standalone overlays
//
// Tooltip behaviour:
// - If minMax exists for the hovered index AND that day had >1 recording (counts[index] > 1),
//   the tooltip shows Min and Max in addition to the point value.
// - Min/Max uses the SAME formatting rules as the Y axis (e.g., mm:ss for time).
//
// Min/Max bar behaviour (requested):
// - If a day effectively has only one value (min == max OR counts[i] <= 1), we DO NOT draw a min/max bar.
//   You just get the normal point.
//
// Important Chart.js note (bug fix):
// - When using {x,y} points with parsing:false, Chart.js expects each data item to be an object.
//   If the array contains literal `null`, Chart.js will try to read `null.y` and crash.
//   Therefore: in time-X mode we ALWAYS emit `{x, y}` objects and represent missing points as `{x, y: null}`.
// =============================================

import React, {
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";

import colours from "../../theme/colours";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { MinMaxBarsPlugin } from "../../utils/plugins/minMaxBars";
import { TimePointSpacingPlugin } from "../../utils/plugins/timePointSpacing";
import Spinner from "../Spinner";
import { useTranslation } from "react-i18next";

// Register once for this module
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  MinMaxBarsPlugin,
  TimePointSpacingPlugin,
);

function MetricTrendChart({
  height = 200,
  labels = [],
  values = [],
  minMax = null,
  counts = null,
  showMinMaxBars = true,
  minMaxWidth = 8,
  lineLabel = "",
  yUnit = "",
  loading = false,
  yMin,
  yMax,
  yTickStep,
  yPadPct = 0.12,
  yPadAbs = 0,
  yDecimals = 0,

  removedIndices: removedIndicesProp,
  onRemovedChange,

  spanGaps = true,

  xValues = null,
  xDomainOverride = null,
  xDomainPaddingPct = 0,
  timePointMinGapPx = 0,
  xTickFormat = "dd/MM",
  xMaxTicksLimit = 6,
  yValueFormat = "number",
  showEmptyPrompt = true,
  showXAxis = true,
  showYAxis = true,
  showYTicks = true,
  showYAxisTitle = true,
  allowPointRemoval = true,
  showMainLine = true,

  showTrendLine = true,
  trendLineWidth = 2,
  highlightPoints = [],
  extraDatasets = [],
  showLegend = true,
}) {
  const { t } = useTranslation();
  const chartRef = useRef(null);

  const hasMainSeriesData =
    Array.isArray(values) && values.some((v) => Number.isFinite(v));
  const hasExtraSeriesData = useMemo(
    () =>
      Array.isArray(extraDatasets) &&
      extraDatasets.some(
        (dataset) =>
          Array.isArray(dataset?.data) &&
          dataset.data.some((value) => Number.isFinite(value)),
      ),
    [extraDatasets],
  );

  // Internal removed set (uncontrolled). If removedIndicesProp is provided, sync to it.
  const [removedSet, setRemovedSet] = useState(() => new Set());
  useEffect(() => {
    if (Array.isArray(removedIndicesProp)) {
      setRemovedSet(new Set(removedIndicesProp));
    }
  }, [removedIndicesProp]);

  // Selection set (visual affordance only). Kept separate from removal.
  const [selectedSet, setSelectedSet] = useState(() => new Set());

  // Keep selection/removal indices valid if data length changes.
  useEffect(() => {
    setSelectedSet((prev) => {
      const next = new Set();
      for (const i of prev)
        if (i >= 0 && i < (values?.length ?? 0)) next.add(i);
      return next;
    });
    setRemovedSet((prev) => {
      const next = new Set();
      for (const i of prev)
        if (i >= 0 && i < (values?.length ?? 0)) next.add(i);
      return next;
    });
  }, [values?.length]);

  const emitRemovedChange = useCallback(
    (nextSet) => {
      if (typeof onRemovedChange === "function") {
        onRemovedChange(Array.from(nextSet).sort((a, b) => a - b));
      }
    },
    [onRemovedChange],
  );

  const formatMmSs = (totalSeconds) => {
    const s = Math.max(0, Math.round(Number(totalSeconds) || 0));
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  // Unified Y value formatter used by BOTH axis ticks and tooltips.
  const formatY = useCallback(
    (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "–";
      if (yValueFormat === "mmss") return formatMmSs(n);
      return n.toFixed(yDecimals);
    },
    [yValueFormat, yDecimals],
  );

  const formatLocalDayMonth = (ms) => {
    const d = new Date(ms);
    const dd = String(d.getUTCDate()).padStart(2, "0");
    const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
    return `${dd}/${mm}`;
  };

  const normaliseXDomain = useCallback(
    (domain, singlePointPad = 12 * 60 * 60 * 1000) => {
      const min = Number(domain?.min);
      const max = Number(domain?.max);

      if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return null;
      }

      if (min === max) {
        return {
          min: min - singlePointPad,
          max: max + singlePointPad,
        };
      }

      return { min, max };
    },
    [],
  );

  // Plot values with removed points nulled out.
  const plottedValues = useMemo(() => {
    if (!Array.isArray(values)) return [];
    return values.map((value, index) =>
      removedSet.has(index) ? null : value,
    );
  }, [values, removedSet]);

  // If xValues are provided and aligned, use time-proportional X.
  const useTimeX =
    Array.isArray(xValues) &&
    xValues.length === plottedValues.length &&
    xValues.some((t) => Number.isFinite(t));

  const parsedHighlightPoints = useMemo(() => {
    if (!Array.isArray(highlightPoints)) {
      return [];
    }

    return highlightPoints
      .map((point) => {
        const y = Number(point?.y);
        if (!Number.isFinite(y)) {
          return null;
        }

        if (useTimeX) {
          const x = Number(point?.x);
          if (!Number.isFinite(x)) {
            return null;
          }

          return {
            label: point?.label || "",
            actualX: x,
            y,
            color: point?.color || colours.secondary,
            borderColor: point?.borderColor || point?.color || colours.secondary,
            borderWidth: Number.isFinite(point?.borderWidth)
              ? Number(point.borderWidth)
              : 0,
            radius: Number.isFinite(point?.radius) ? Number(point.radius) : 5,
            hoverRadius: Number.isFinite(point?.hoverRadius)
              ? Number(point.hoverRadius)
              : 7,
          };
        }

        const index = Number(point?.index);
        if (!Number.isInteger(index) || index < 0) {
          return null;
        }

        return {
          label: point?.label || "",
          index,
          y,
          color: point?.color || colours.secondary,
          borderColor: point?.borderColor || point?.color || colours.secondary,
          borderWidth: Number.isFinite(point?.borderWidth)
            ? Number(point.borderWidth)
            : 0,
          radius: Number.isFinite(point?.radius) ? Number(point.radius) : 5,
          hoverRadius: Number.isFinite(point?.hoverRadius)
            ? Number(point.hoverRadius)
            : 7,
        };
      })
      .filter(Boolean);
  }, [highlightPoints, useTimeX]);

  const hasHighlightPointData = parsedHighlightPoints.length > 0;
  const hasAnyData =
    hasMainSeriesData || hasExtraSeriesData || hasHighlightPointData;

  const applyXDomainPadding = useCallback(
    (domain) => {
      if (!domain) {
        return null;
      }

      const paddingPct = Number(xDomainPaddingPct);
      if (!Number.isFinite(paddingPct) || paddingPct <= 0) {
        return domain;
      }

      const span = Math.max(domain.max - domain.min, 1);
      const pad = span * paddingPct;

      return {
        min: domain.min - pad,
        max: domain.max + pad,
      };
    },
    [xDomainPaddingPct],
  );

  // Compute the real x-domain from the plotted data (no artificial padding).
  const computedXDomain = useMemo(() => {
    if (!useTimeX) return null;

    const xs = [];
    const n = Math.min(plottedValues.length, xValues?.length ?? 0);

    for (let i = 0; i < n; i += 1) {
      if (removedSet.has(i)) continue;

      const x = Number(xValues?.[i]);
      const y = plottedValues[i];

      // Only include points that actually exist on the line
      if (!Number.isFinite(x)) continue;
      if (!Number.isFinite(y)) continue;

      xs.push(x);
    }

    if (!xs.length) return null;

    return normaliseXDomain({
      min: Math.min(...xs),
      max: Math.max(...xs),
    });
  }, [
    normaliseXDomain,
    plottedValues,
    removedSet,
    useTimeX,
    xValues,
  ]);

  const xDomain = useMemo(() => {
    if (!useTimeX) return null;
    return applyXDomainPadding(
      normaliseXDomain(xDomainOverride) || computedXDomain,
    );
  }, [
    applyXDomainPadding,
    computedXDomain,
    normaliseXDomain,
    useTimeX,
    xDomainOverride,
  ]);

  // Decide whether to draw a min/max bar at index i.
  // Requested: if it's effectively a single value (min==max OR counts<=1) -> no bar.
  const shouldDrawMinMaxBarAt = useCallback(
    (i) => {
      if (!Array.isArray(minMax) || !minMax[i]) return false;

      const c =
        Array.isArray(counts) && Number.isFinite(counts[i])
          ? Number(counts[i])
          : null;

      if (c != null && c <= 1) return false;

      const mm = minMax[i];
      const lo = Array.isArray(mm) ? mm[0] : mm?.min;
      const hi = Array.isArray(mm) ? mm[1] : mm?.max;

      if (!Number.isFinite(lo) || !Number.isFinite(hi)) return false;
      if (Number(lo) === Number(hi)) return false;

      return true;
    },
    [minMax, counts],
  );

  // IMPORTANT: Only pass bars to the plugin where we actually want to draw them.
  // Everywhere else: null => plugin should skip drawing, leaving the normal point.
  const minMaxForBars = useMemo(() => {
    if (!Array.isArray(minMax)) return null;
    return minMax.map((mm, i) => (shouldDrawMinMaxBarAt(i) ? mm : null));
  }, [minMax, shouldDrawMinMaxBarAt]);

  // Collect numbers influencing the Y auto-range, excluding removed/missing points.
  const [seriesMin, seriesMax] = useMemo(() => {
    const nums = [];

    for (let i = 0; i < (plottedValues || []).length; i += 1) {
      const v = plottedValues[i];
      if (Number.isFinite(v)) nums.push(v);
    }

    if (Array.isArray(minMax)) {
      for (let i = 0; i < minMax.length; i += 1) {
        if (removedSet.has(i)) continue;
        const mm = minMax[i];
        const lo = Array.isArray(mm) ? mm[0] : mm?.min;
        const hi = Array.isArray(mm) ? mm[1] : mm?.max;
        if (Number.isFinite(lo)) nums.push(lo);
        if (Number.isFinite(hi)) nums.push(hi);
      }
    }

    if (!nums.length) return [0, 1];
    return [Math.min(...nums), Math.max(...nums)];
  }, [plottedValues, minMax, removedSet]);

  const roundDownTo = (v, step) => Math.floor(v / step) * step;
  const roundUpTo = (v, step) => Math.ceil(v / step) * step;

  const [minAuto, maxAuto, autoTickStep] = useMemo(() => {
    let min = seriesMin;
    let max = seriesMax;
    let step =
      Number.isFinite(yTickStep) && yTickStep > 0 ? Number(yTickStep) : null;

    const span = Math.max(max - min, 1);
    const pad = Math.max(span * yPadPct, yPadAbs);
    min -= pad;
    max += pad;

    if (step != null) {
      min = roundDownTo(min, step);
      max = roundUpTo(max, step);
    } else {
      const span2 = Math.max(max - min, 1e-9);
      const pow = Math.pow(10, Math.floor(Math.log10(span2)));
      const niceStep = span2 / 6;
      const candidates = [1, 2, 5].map((m) => m * pow);
      const minimumFormattedStep =
        yValueFormat === "mmss"
          ? 1
          : Math.pow(10, -Math.max(0, Number(yDecimals) || 0));

      step = candidates.reduce(
        (best, c) =>
          Math.abs(c - niceStep) < Math.abs(best - niceStep) ? c : best,
        candidates[0],
      );
      step = Math.max(step, minimumFormattedStep);
      min = Math.floor(min / step) * step;
      max = Math.ceil(max / step) * step;
    }

    if (min === max) {
      const fallbackStep = step || 1;
      min -= fallbackStep;
      max += fallbackStep;
      step = fallbackStep;
    }

    return [min, max, step || 1];
  }, [seriesMin, seriesMax, yPadPct, yPadAbs, yTickStep, yValueFormat, yDecimals]);

  const primary = getPrimaryColour();

  // Per-point style: highlight selected indices via radius.
  const pointRadius = useMemo(() => {
    const n = plottedValues.length || (labels?.length ?? 0);
    return Array.from({ length: n }, (_, index) => {
      if (!hasMainSeriesData) return 0;
      return allowPointRemoval && selectedSet.has(index) ? 6 : 3;
    });
  }, [
    plottedValues.length,
    labels?.length,
    hasMainSeriesData,
    allowPointRemoval,
    selectedSet,
  ]);

  const pointBg = useMemo(() => {
    const n = plottedValues.length || (labels?.length ?? 0);
    return new Array(n).fill(primary);
  }, [plottedValues.length, labels?.length, primary]);

  const trendLine = useMemo(() => {
    if (!showTrendLine || !hasMainSeriesData) return null;

    const pts = [];
    const n = Math.min(
      plottedValues.length,
      xValues?.length ?? plottedValues.length,
    );

    for (let i = 0; i < n; i += 1) {
      const y = Number(plottedValues[i]);
      if (!Number.isFinite(y)) continue;

      const x = useTimeX ? Number(xValues?.[i]) : i;
      if (!Number.isFinite(x)) continue;

      pts.push({ x, y });
    }

    if (pts.length < 2) return null;

    // least squares: y = a + b x
    let sumX = 0,
      sumY = 0,
      sumXX = 0,
      sumXY = 0;
    for (const p of pts) {
      sumX += p.x;
      sumY += p.y;
      sumXX += p.x * p.x;
      sumXY += p.x * p.y;
    }

    const denom = pts.length * sumXX - sumX * sumX;
    if (Math.abs(denom) < 1e-9) return null;

    const b = (pts.length * sumXY - sumX * sumY) / denom;
    const a = (sumY - b * sumX) / pts.length;

    const xMin = Math.min(...pts.map((p) => p.x));
    const xMax = Math.max(...pts.map((p) => p.x));

    const yMin = a + b * xMin;
    const yMax = a + b * xMax;

    return { xMin, xMax, yMin, yMax };
  }, [
    showTrendLine,
    hasMainSeriesData,
    plottedValues,
    useTimeX,
    xValues,
  ]);

  // Build dataset values:
  // - category X: raw numbers/nulls are fine
  // - time X: MUST be objects (no literal null entries)
  const chartData = useMemo(() => {
    const emptyTimeSeriesPointCount =
      useTimeX && Array.isArray(xValues) && xValues.length > 0 ? xValues.length : 0;
    const safeLabels =
      labels?.length > 0
        ? labels
        : useTimeX && emptyTimeSeriesPointCount > 0
          ? new Array(emptyTimeSeriesPointCount).fill("")
          : hasMainSeriesData
          ? new Array(plottedValues.length).fill("")
          : ["", "", "", "", "", ""];

    const n = safeLabels.length || 6;

    let mainDataArr;
    if (!hasMainSeriesData && useTimeX) {
      mainDataArr = Array.from({ length: n }, (_, i) => {
        const actualX = Number(xValues?.[i]);
        return {
          x: Number.isFinite(actualX) ? actualX : null,
          actualX: Number.isFinite(actualX) ? actualX : null,
          y: null,
        };
      });
    } else if (!hasMainSeriesData) {
      mainDataArr = new Array(n).fill(null);
    } else if (useTimeX) {
      mainDataArr = plottedValues.map((y, i) => {
        const actualX = Number(xValues?.[i]);
        const xSafe = Number.isFinite(actualX) ? actualX : null;
        const ySafe = Number.isFinite(y) ? y : null;
        return {
          x: xSafe,
          actualX: Number.isFinite(actualX) ? actualX : null,
          y: xSafe == null ? null : ySafe,
        };
      });
    } else {
      mainDataArr = plottedValues;
    }

    const mainDs = {
      label: lineLabel || yUnit || "Trend",
      data: mainDataArr,
      borderColor: primary,
      pointBackgroundColor: pointBg,
      pointBorderColor: primary,
      pointRadius,
      pointHoverRadius: hasMainSeriesData ? 6 : 0,
      borderWidth: showMainLine ? 2 : 0,
      showLine: showMainLine,
      spanGaps,
      tension: 0,
      parsing: useTimeX ? false : true,

      // MinMaxBarsPlugin reads this custom property
      ...(Array.isArray(minMaxForBars) && minMaxForBars.length === n
        ? { _minMax: minMaxForBars }
        : {}),
    };

    const datasets = [mainDs];

    if (Array.isArray(extraDatasets) && extraDatasets.length) {
      extraDatasets.forEach((dataset) => {
        const rawData = Array.isArray(dataset?.data)
          ? dataset.data
          : new Array(n).fill(null);
        const datasetHasData = rawData.some((value) => Number.isFinite(value));
        const builtData = useTimeX
          ? Array.from({ length: n }, (_, i) => {
              const actualX = Number(xValues?.[i]);
              const rawY = rawData[i];
              const y =
                typeof rawY === "number" && Number.isFinite(rawY)
                  ? rawY
                  : null;
              const xSafe = Number.isFinite(actualX) ? actualX : null;
              const ySafe = y;
              return {
                x: xSafe,
                actualX: Number.isFinite(actualX) ? actualX : null,
                y: xSafe == null ? null : ySafe,
              };
            })
          : rawData.map((value) =>
              typeof value === "number" && Number.isFinite(value)
                ? value
                : null,
            );

        datasets.push({
          label: dataset?.label || "",
          data: builtData,
          borderColor: dataset?.color || primary,
          pointBackgroundColor: dataset?.color || primary,
          pointBorderColor: dataset?.color || primary,
          pointRadius: datasetHasData ? (dataset?.pointRadius ?? 2) : 0,
          pointHoverRadius: datasetHasData ? (dataset?.hoverRadius ?? 4) : 0,
          borderWidth: dataset?.borderWidth ?? 2,
          spanGaps,
          tension: 0,
          parsing: useTimeX ? false : true,
        });
      });
    }

    if (trendLine) {
      const trendDs = {
        label: t("charts.trend.trendLine", "Trend"),
        type: "line",
        data: useTimeX
          ? [
              { x: trendLine.xMin, y: trendLine.yMin },
              { x: trendLine.xMax, y: trendLine.yMax },
            ]
          : [trendLine.yMin, trendLine.yMax], // category mode uses indexes 0..n; we need labels for endpoints
        parsing: useTimeX ? false : true,
        borderColor: "rgba(16, 122, 146, 0.7)",
        borderWidth: trendLineWidth,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0,
        spanGaps: true,
      };

      // If NOT time-x, pin the trend line to first and last indices
      if (!useTimeX) {
        const nPts = safeLabels.length || 6;
        const arr = new Array(nPts).fill(null);
        arr[0] = trendLine.yMin;
        arr[nPts - 1] = trendLine.yMax;
        trendDs.data = arr;
      }

      // Draw trend behind points
      datasets.push(trendDs);
    }

    if (parsedHighlightPoints.length) {
      parsedHighlightPoints.forEach((point) => {
        const data = useTimeX
          ? [{ x: point.actualX, actualX: point.actualX, y: point.y }]
          : Array.from({ length: n }, (_, index) =>
              index === point.index ? point.y : null,
            );

        datasets.push({
          label: point.label,
          data,
          borderColor: point.borderColor,
          pointBackgroundColor: point.color,
          pointBorderColor: point.borderColor,
          pointBorderWidth: point.borderWidth,
          pointRadius: point.radius,
          pointHoverRadius: point.hoverRadius,
          borderWidth: 0,
          showLine: false,
          spanGaps: false,
          tension: 0,
          parsing: useTimeX ? false : true,
        });
      });
    }

    return { labels: safeLabels, datasets };
  }, [
    labels,
    hasMainSeriesData,
    plottedValues,
    useTimeX,
    xValues,
    minMaxForBars,
    lineLabel,
    yUnit,
    t,
    primary,
    pointRadius,
    pointBg,
    showMainLine,
    spanGaps,
    trendLine,
    trendLineWidth,
    extraDatasets,
    parsedHighlightPoints,
  ]);

  const options = useMemo(() => {
    const formatXTick = (tickValue) => {
      if (!useTimeX) return labels?.[tickValue] ?? tickValue;
      if (xTickFormat === "dd/MM")
        return Number.isFinite(Number(tickValue))
          ? formatLocalDayMonth(Number(tickValue))
          : "";
      return Number.isFinite(Number(tickValue))
        ? formatLocalDayMonth(Number(tickValue))
        : "";
    };

    const getMinMaxAt = (index) => {
      if (!Array.isArray(minMax) || index == null) return null;
      const mm = minMax[index];
      if (!mm) return null;
      const lo = Array.isArray(mm) ? mm[0] : mm?.min;
      const hi = Array.isArray(mm) ? mm[1] : mm?.max;
      return {
        min: Number.isFinite(lo) ? Number(lo) : null,
        max: Number.isFinite(hi) ? Number(hi) : null,
      };
    };

    const shouldShowMinMaxInTooltip = (index) => {
      const c =
        Array.isArray(counts) && Number.isFinite(counts[index])
          ? Number(counts[index])
          : null;

      if (c != null) return c > 1;

      const mm = getMinMaxAt(index);
      if (!mm || mm.min == null || mm.max == null) return false;
      return mm.min !== mm.max;
    };

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: "bottom", // "top" | "left" | "bottom" | "right"
          align: "start", // "start" | "center" | "end"
          labels: {
            boxWidth: 10,
            boxHeight: 10,
            usePointStyle: true,
          },
        },
        tooltip: {
          enabled: hasAnyData,
          callbacks: {
            title: (items) => {
              const it = items?.[0];
              if (!it) return "";
              const idx = it.dataIndex;

              if (useTimeX) {
                const x = it.raw?.actualX ?? it.parsed?.x;
                return Number.isFinite(x) ? formatLocalDayMonth(Number(x)) : "";
              }

              return typeof labels?.[idx] === "string" ? labels[idx] : "";
            },

            label: (ctx) => {
              const y = ctx.parsed?.y;
              if (!Number.isFinite(y)) return "";

              const name =
                ctx.dataset?.label ||
                lineLabel ||
                yUnit ||
                t("results.value", "Value");
              const val = formatY(y);
              return yUnit ? `${name}: ${val} ${yUnit}` : `${name}: ${val}`;
            },

            afterBody: (items) => {
              const it = items?.find((x) => x?.datasetIndex === 0);
              if (!it) return [];

              const idx = it.dataIndex;
              if (!shouldShowMinMaxInTooltip(idx)) return [];

              const mm = getMinMaxAt(idx);
              if (!mm || mm.min == null || mm.max == null) return [];
              if (mm.min === mm.max) return []; // single value => no annotation lines

              const lines = [
                `${t("charts.trend.min", "Min")}: ${formatY(mm.min)}${yUnit ? ` ${yUnit}` : ""}`,
                `${t("charts.trend.max", "Max")}: ${formatY(mm.max)}${yUnit ? ` ${yUnit}` : ""}`,
              ];

              const c =
                Array.isArray(counts) && Number.isFinite(counts[idx])
                  ? Number(counts[idx])
                  : null;

              if (c != null) {
                lines.push(
                  t("charts.trend.scans", "Scans: {{count}}", { count: c }),
                );
              }

              return lines;
            },
          },
        },
        ...(showMinMaxBars
          ? {
              minMaxBars: {
                enabled: true,
                lineWidth: minMaxWidth,
                color: getPrimaryColour({ format: "rgb", opacity: 0.7 }),
                capStyle: "round",
              },
            }
          : {}),
        ...(useTimeX && Number(timePointMinGapPx) > 0
          ? {
              timePointSpacing: {
                enabled: true,
                minGapPx: Number(timePointMinGapPx),
                gutterPx: 2,
              },
            }
          : {}),
      },
      scales: {
        x: {
          display: showXAxis,
          type: useTimeX ? "linear" : "category",
          // IMPORTANT: prevent Chart.js from choosing "nice" outer bounds (which looks like fake dates)
          ...(useTimeX && xDomain
            ? {
                min: xDomain.min,
                max: xDomain.max,
                bounds: "data",
              }
            : {}),
          // Only matters for category scales, but keeping explicit avoids surprises
          offset: false,

          grid: { color: "rgba(0,0,0,0.05)", drawBorder: false },
          border: { display: false },
          ticks: {
            color: "rgba(0,0,0,0.5)",
            autoSkip: true,
            maxTicksLimit: xMaxTicksLimit,
            callback: (v) => formatXTick(v),
          },
          title: { display: false },
        },
        y: {
          display: showYAxis,
          grace: "8%",
          grid: { color: "rgba(0,0,0,0.06)", drawBorder: false },
          border: { display: false },
          min: Number.isFinite(yMin) ? yMin : minAuto,
          max: Number.isFinite(yMax) ? yMax : maxAuto,
          ticks: {
            color: "rgba(0,0,0,0.5)",
            stepSize: autoTickStep,
            callback: (v) => {
              if (!showYTicks) return "";
              const n = Number(v);
              if (!Number.isFinite(n)) return v;
              return formatY(n);
            },
          },
          title: {
            display: showYAxisTitle && !!yUnit,
            text: yUnit,
            color: "rgba(0,0,0,0.65)",
          },
        },
      },
      animation: { duration: 250 },
      interaction: { mode: "nearest", intersect: true },
      hover: { mode: "nearest", intersect: true },
    };
  }, [
    hasAnyData,
    labels,
    timePointMinGapPx,
    useTimeX,
    xTickFormat,
    xMaxTicksLimit,
    xDomainPaddingPct,
    minMax,
    counts,
    showMinMaxBars,
    showLegend,
    showXAxis,
    showYAxis,
    minMaxWidth,
    yMin,
    yMax,
    minAuto,
    maxAuto,
    autoTickStep,
    yUnit,
    showYTicks,
    showYAxisTitle,
    lineLabel,
    formatY,
    t,
    xDomain,
  ]);

  const onClick = useCallback(
    (event) => {
      const chart = chartRef.current;
      if (!chart) return;

      const elements = chart.getElementsAtEventForMode(
        event.nativeEvent,
        "nearest",
        { intersect: true },
        true,
      );

      if (!elements?.length) {
        setSelectedSet(new Set());
        return;
      }

      const { index, datasetIndex } = elements[0];

      // Only toggle removal for the main dataset
      if (datasetIndex !== 0) return;

      const multi =
        event.nativeEvent.shiftKey ||
        event.nativeEvent.metaKey ||
        event.nativeEvent.ctrlKey;

      setSelectedSet((prev) => {
        const next = multi ? new Set(prev) : new Set();
        if (next.has(index)) next.delete(index);
        else next.add(index);
        return next;
      });

      setRemovedSet((prev) => {
        const next = new Set(prev);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        emitRemovedChange(next);
        return next;
      });
    },
    [emitRemovedChange],
  );

  return (
    <div className="relative" style={{ height }}>
      {!hasAnyData && !loading && showEmptyPrompt ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none">
          <div className="text-xs text-typography/60">
            {t("results.emptyRecordPrompt", "No data yet")}
          </div>
        </div>
      ) : null}

      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <Spinner />{" "}
        </div>
      )}

      <Line
        ref={chartRef}
        data={chartData}
        options={options}
        onClick={allowPointRemoval ? onClick : undefined}
      />
    </div>
  );
}

export default memo(MetricTrendChart);
